import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import {
    DeepPartial,
    FieldErrors,
    FormProvider,
    UnpackNestedValue,
    useForm,
    useFormContext,
    useFormState,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, CircularProgress } from '@mui/material';
import { reach } from 'yup';

export interface IRhFormWrapperProps<T> {
    initialValues?: UnpackNestedValue<DeepPartial<T>>;
    validationSchema: any;
    onSubmit: (
        data: UnpackNestedValue<T>,
        event?: React.BaseSyntheticEvent,
    ) => Promise<void>;
    onInvalid?: (errors: FieldErrors<T>) => Promise<void>;
    children: React.ReactNode | React.ReactNode[];
    disabled?: boolean;
    // Run validation across every field as soon as the form mounts (instead of
    // waiting for a change/submit). Left undefined it DEFAULTS to "editing" — i.e.
    // on when the initial values carry a truthy `id` (a pre-existing entity), off
    // when creating — so stale data already in a record surfaces immediately on
    // open. Pass an explicit boolean only to force the behavior either way.
    validateOnMount?: boolean;
    // Optional content pinned to the LEFT of the sticky action bar, opposite
    // the submit button. Intended for read-only footer metadata (e.g. the
    // created_by / updated_by stamps) — it inherits the bar's padding, so it
    // lines up with the form body instead of hugging the dialog edge.
    actionBarStart?: React.ReactNode;
}

interface IRhCustomContext {
    disabled: boolean;
    // Returns true when the field at `name` is declared required in the
    // form's yup schema. Used by RhTabsWrapper to compute its "n / x" counter.
    isFieldRequired: (name: string) => boolean;
}

const RhCustomContext = React.createContext<IRhCustomContext>({
    disabled: false,
    isFieldRequired: () => false,
});

export const useRhCustomContext = () => {
    return useContext(RhCustomContext);
};

// Flattens react-hook-form's nested error tree into the dot paths `trigger`
// takes (`order_sale_products.0.kilo_price`). A leaf is a FieldError, i.e. the
// `{ type, message, ref }` shape; anything else is a branch to walk. Array
// indices come out as plain keys, which is exactly how RHF addresses them.
function collectErrorPaths(errors: unknown, prefix = ''): string[] {
    if (!errors || typeof errors !== 'object') {
        return [];
    }

    const node = errors as Record<string, unknown>;

    if (typeof node.message === 'string' || typeof node.type === 'string') {
        return prefix ? [prefix] : [];
    }

    const paths: string[] = [];
    Object.keys(node).forEach((key) => {
        const childPrefix = prefix ? `${prefix}.${key}` : key;
        collectErrorPaths(node[key], childPrefix).forEach((path) => {
            paths.push(path);
        });
    });
    return paths;
}

// Re-checks every field that is CURRENTLY showing an error whenever any value
// changes.
//
// react-hook-form runs the whole yup schema on each change, but then writes back
// only the error belonging to the field that changed (schemaErrorLookup in
// createFormControl). Cross-field rules therefore go stale: with "Precio por
// kilo / Precio por grupo" — set exactly one of the two — filling the kilo price
// clears its own error and leaves the group price still red, even though the
// schema already considers the pair valid. The same applies to every paired rule
// and to the uniqueness rules that span rows of a column list.
//
// `trigger(paths)` re-runs the schema and both sets AND clears the errors for
// exactly those paths. Restricting it to paths that already carry an error is
// what keeps this from becoming "validate everything": a create form the user has
// barely started stays quiet, because there is nothing to re-check yet.
//
// Lives in its own component, rendered inside FormProvider, so the error
// subscription sits on a node that renders nothing. Subscribing RhFormWrapper
// itself would re-render it on every keystroke, and since it spreads a fresh
// context value into FormProvider that would re-render every input on the form.
function RhErrorSync() {
    const { control, watch, trigger } = useFormContext();
    const { errors } = useFormState({ control });

    const errorsRef = useRef(errors);
    errorsRef.current = errors;

    useEffect(() => {
        const subscription = watch((_values, { name }) => {
            // `reset` publishes on this channel too, but without a field name.
            // Skip it: a reset clears the errors deliberately, and re-checking
            // the paths it just dropped would put them straight back.
            if (!name) {
                return;
            }

            const erroredPaths = collectErrorPaths(errorsRef.current);
            if (erroredPaths.length > 0) {
                void trigger(erroredPaths as Parameters<typeof trigger>[0]);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, trigger]);

    return null;
}

export function RhFormWrapper<T>({
    initialValues,
    onSubmit,
    validationSchema,
    onInvalid,
    children,
    disabled = false,
    validateOnMount,
    actionBarStart,
}: IRhFormWrapperProps<T>) {
    const rhObject = useForm<T>({
        defaultValues: initialValues,
        resolver: yupResolver(validationSchema),
        reValidateMode: 'onChange',
        mode: 'onChange',
        shouldFocusError: true,
        shouldUseNativeValidation: false,
    });

    // "Editing" = the form opened on a pre-existing entity, whose initial values
    // carry a truthy `id` (create forms have none). Validate-on-mount defaults to
    // this, so an edit dialog flags any already-invalid data (a drifted price, a
    // subtotal that no longer matches its resources, a removed catalog product, …)
    // the moment it opens, not just on submit. An explicit prop overrides it.
    const isEditing = !!(
        initialValues as unknown as { id?: number | null } | undefined
    )?.id;
    const shouldValidateOnMount = validateOnMount ?? isEditing;

    useEffect(() => {
        void rhObject.reset(initialValues);
    }, [rhObject, initialValues]);

    // Validate every field once on mount when editing (or when forced). Deferred
    // a tick so it runs against the values the reset effect above just applied.
    useEffect(() => {
        if (!shouldValidateOnMount) return;
        const id = setTimeout(() => void rhObject.trigger(), 0);
        return () => clearTimeout(id);
    }, [rhObject, initialValues, shouldValidateOnMount]);

    // Walk the yup schema for a given field path and report whether it carries
    // a `required` test. `reach` resolves dot/array paths (e.g. nested objects),
    // and `describe().tests` lists the validations attached to that field.
    const isFieldRequired = useCallback(
        (name: string): boolean => {
            try {
                const fieldSchema: any = reach(validationSchema, name);
                const description = fieldSchema?.describe?.();
                return Boolean(
                    description?.tests?.some(
                        (test: { name?: string }) => test.name === 'required',
                    ),
                );
            } catch {
                return false;
            }
        },
        [validationSchema],
    );

    const contextValue = useMemo<IRhCustomContext>(
        () => ({ disabled, isFieldRequired }),
        [disabled, isFieldRequired],
    );

    // `isSubmitting` is true for as long as the async `onSubmit` promise is
    // pending (handleSubmit awaits it). Reading it here subscribes the form to
    // it, so the submit button can disable + show a spinner and the user can't
    // fire the mutation twice with a double-click. Reading the proxy field in
    // render is what enables the subscription in react-hook-form.
    const { isSubmitting } = rhObject.formState;

    return (
        <RhCustomContext.Provider value={contextValue}>
            <FormProvider {...rhObject}>
                <Box
                    component={'form'}
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                    }}
                    onSubmit={rhObject.handleSubmit(onSubmit, onInvalid)}
                >
                    {/* Renders nothing; keeps the cross-field error resync
                        subscribed here instead of on RhFormWrapper. Sits inside
                        the form element so FormProvider keeps a single child. */}
                    <RhErrorSync />
                    <Box sx={{ flex: 1 }}>{children}</Box>
                    {/* Sticky action bar pinned to the bottom of the dialog's
                        scroll container (the DModal body), so it stays visible as
                        the form scrolls. Full width with a solid background and a
                        top border so content scrolls underneath it cleanly. */}
                    <Box
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            py: 2,
                            px: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            justifyContent: 'space-between',
                            bgcolor: 'background.paper',
                            borderTop: 1,
                            borderColor: 'divider',
                            zIndex: 1,
                        }}
                    >
                        {/* Always rendered (even when empty) so space-between
                            keeps the submit button pinned right. */}
                        <Box sx={{ minWidth: 0 }}>{actionBarStart ?? null}</Box>
                        <Button
                            disabled={disabled || isSubmitting}
                            variant={'contained'}
                            type={'submit'}
                            startIcon={
                                isSubmitting ? (
                                    <CircularProgress
                                        size={16}
                                        color={'inherit'}
                                    />
                                ) : undefined
                            }
                        >
                            {isSubmitting ? 'Guardando...' : 'Aceptar'}
                        </Button>
                    </Box>
                </Box>
            </FormProvider>
        </RhCustomContext.Provider>
    );
}
